import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize, tap } from 'rxjs';
import { DevToolsService } from './dev-tools.service';
import { DevToolsEventLevel } from './dev-tools.types';

const MAX_ARRAY_ITEMS = 25;
const MAX_STRING_LENGTH = 2_000;
const MAX_DEPTH = 12;

/**
 * Tronque un payload avant stockage dans le ring buffer du DevTools :
 * les réponses de liste (limit=1000…) exploseraient la mémoire du panel.
 */
function truncate(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH
      ? value.slice(0, MAX_STRING_LENGTH) + `… [tronqué, ${value.length} chars]`
      : value;
  }
  if (typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return '[… profondeur max]';
  if (Array.isArray(value)) {
    const head = value.slice(0, MAX_ARRAY_ITEMS).map((v) => truncate(v, depth + 1));
    if (value.length > MAX_ARRAY_ITEMS) {
      head.push(`… [tronqué, ${value.length} items au total]`);
    }
    return head;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = truncate(v, depth + 1);
  }
  return out;
}

function levelForStatus(status: number): DevToolsEventLevel {
  if (status >= 500 || status === 0) return 'error';
  if (status >= 400) return 'warn';
  if (status >= 200 && status < 300) return 'success';
  return 'info';
}

/**
 * Intercepteur DevTools : trace chaque requête HttpClient (toutes pages, tous
 * modules) dans l'onglet HTTP du panel. No-op quand DevToolsService.enabled
 * est false (prod, SSR) — passthrough sans aucun coût.
 */
export const devToolsHttpInterceptor: HttpInterceptorFn = (req, next) => {
  const dev = inject(DevToolsService);
  if (!dev.enabled) return next(req);

  const startedAt = Date.now();
  let settled = false;

  const push = (
    level: DevToolsEventLevel,
    outcome: 'ok' | 'error' | 'cancelled',
    detail: Record<string, unknown>
  ): void => {
    settled = true;
    dev.pushEvent({
      source: 'http',
      type: `${req.method} ${req.url.split('?')[0]}`,
      level,
      payload: {
        method: req.method,
        url: req.urlWithParams,
        outcome,
        durationMs: Date.now() - startedAt,
        requestBody: req.body !== null ? truncate(req.body) : undefined,
        ...detail,
      },
    });
  };

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          push(levelForStatus(event.status), 'ok', {
            status: event.status,
            statusText: event.statusText,
            responseBody: truncate(event.body),
          });
        }
      },
      error: (err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          push(levelForStatus(err.status), 'error', {
            status: err.status,
            statusText: err.statusText,
            message: err.message,
            responseBody: truncate(err.error),
          });
        } else {
          push('error', 'error', {
            status: 0,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      },
    }),
    finalize(() => {
      // Ni réponse ni erreur : requête annulée (navigation, unsubscribe, switchMap…)
      if (!settled) {
        push('warn', 'cancelled', { status: 0, statusText: 'cancelled' });
      }
    })
  );
};
